<template>
  <a-spin :spinning="loading">
    <div class="bots-list">
      <BotCard create @create="handleShowCreate" />

      <BotCard
        v-for="bot in botsList"
        :key="bot.id"
        :bot="bot"
        @edit="goToBotDetails(bot.id)"
        @duplicate="handleCopy(bot.id, bot.name)"
        @update="handleUpdate"
      />
    </div>
  </a-spin>

  <CreateBotModal
    v-if="createModalVisible"
    v-model:visible="createModalVisible"
    :isAutomatic="isAutomatic"
    @goToDetails="goToBotDetails"
  />

  <RestrictionChangePlanModal
    title="Realiza un upgrade y accede a todo el poder de Beex."
    v-model:open="openRestriction"
  >
    <template #description>
      Tu plan {{ PlanTypeText[plan] }} no incluye este módulo.
      Solicita un upgrade tu plan para potenciar tu ambiente de trabajo.
    </template>
  </RestrictionChangePlanModal>
</template>

<script setup lang="ts">
import { computed, createVNode, onMounted, onUnmounted, ref, watch } from "vue";
import { Modal, message } from "ant-design-vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";
import { useRoute, useRouter } from "vue-router";

import { Bot } from "@/@types/ivr";
import { botServices } from "@/app/ivr/bot/services/BotServices";
import { ResponseError } from "@/app/shared/utils/httpManager";

import BotCard from "@/app/ivr/bot/components/cards/BotCard.vue";
import CreateBotModal from "@/app/ivr/bot/components/modals/CreateBotModal.vue";
import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import { PlanType, PlanTypeText } from "@/app/common/types";
import { useStore } from "vuex";
import { ActionPlanStrategy } from "@/app/common/utils/enums";
import EventBus from "@/app/shared/utils/eventBus";

defineProps<{ botsList: Bot[]; loading: boolean }>();

const emit = defineEmits<{
  (e: "update"): void;
}>();

const route = useRoute();
const router = useRouter();

const store = useStore();

const user = computed(() => store.getters["getDataUser"]);
const plan = computed(() => user.value?.change_custom_plan?.plan);
const openRestriction = ref(false);

const createModalVisible = ref(false);

const isAutomatic = route.path.includes("ivr-automatic");

const validateActionPlanHeader = () => {
  if (
    (isAutomatic && plan.value == PlanType.Digital) ||
    (!isAutomatic && [PlanType.Voice, PlanType.Digital].includes(plan.value))
  ) {
    store.commit("SET_ACTION_PLAN_HEADER", ActionPlanStrategy.Upgrade);
  } else {
    store.commit("SET_ACTION_PLAN_HEADER", null);
  }
};

EventBus.on("SHOW_RESTRICTION_PLAN", () => {
  openRestriction.value = true
});

watch(user, () => {
  validateActionPlanHeader();
})

watch(plan, () => {
  validateActionPlanHeader();
});

onMounted(() => {
  validateActionPlanHeader();
});

onUnmounted(() => {
  store.commit("SET_ACTION_PLAN_HEADER", null);
});

const handleShowCreate = () => {
  if (
    (isAutomatic && plan.value == PlanType.Digital) ||
    (!isAutomatic && [PlanType.Voice, PlanType.Digital].includes(plan.value))
  )
    openRestriction.value = true
  else
    createModalVisible.value = true;
}

const goToBotDetails = (id: number) => {
  if (isAutomatic) router.push({
    name: "edit-bot",
    params: {id: id},
    query: route.query,
  });
  else router.push(`ivr-survey/${id}`);
};

const handleUpdate = () => emit("update");

const handleCopy = async (id: number, botName: string) => {
  Modal.confirm({
    centered: true,
    icon: createVNode(QuestionCircleOutlined),
    title: `¿Estás seguro de duplicar el IVR "${botName}"?`,
    content:
      "Se duplicará toda la configuración y el flujo de este IVR. Recuerda cambiar el horario del nuevo IVR para que no interfiera con el original.",
    cancelText: "Cancelar",
    okText: "Duplicar",
    onOk: async () => {
      try {
        await botServices.duplicate(id);
        handleUpdate();
      } catch (error) {
        const errorMessage =
          error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

        message.error(errorMessage);
      }
    },
  });
};
</script>

<style scoped lang="sass">
.bots-list
  width: calc(100% - 10px)
  gap: 12px
  display: grid
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr))
</style>
